import { Link } from 'react-router-dom';

const page: React.CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  padding: '48px 24px 80px',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  color: '#1f2937',
  lineHeight: 1.7,
};

const h1Style: React.CSSProperties = {
  fontSize: 28,
  fontWeight: 800,
  color: '#111827',
  margin: '0 0 8px',
};

const metaStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#6b7280',
  margin: '0 0 40px',
};

const h2Style: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: '#111827',
  margin: '36px 0 10px',
};

const pStyle: React.CSSProperties = {
  margin: '0 0 14px',
  fontSize: 15,
};

const ulStyle: React.CSSProperties = {
  margin: '0 0 14px',
  paddingLeft: 20,
  fontSize: 15,
};

const todoStyle: React.CSSProperties = {
  background: '#fef9c3',
  border: '1px solid #fde047',
  borderRadius: 6,
  padding: '6px 10px',
  fontSize: 13,
  color: '#854d0e',
  display: 'inline-block',
};

export function PrivacyPolicy() {
  return (
    <div style={page}>
      <Link to="/" style={{ display: 'inline-block', fontSize: 13, color: '#6b7280', textDecoration: 'none', marginBottom: 32 }}>
        ← Back to Housewizrd
      </Link>

      <h1 style={h1Style}>Privacy Policy</h1>
      <p style={metaStyle}>Last updated: July 1, 2026 · Housewizrd LLC, Nahunta, Georgia</p>

      <p style={pStyle}>
        This Privacy Policy explains how Housewizrd LLC ("Housewizrd," "we," "our," or "us") collects,
        uses, and shares information when you use the Housewizrd mobile app and website (collectively,
        the "Service"). By using the Service you agree to this policy.
      </p>
      <p style={pStyle}>
        Housewizrd is currently in early access. Some features and data practices described here may
        evolve as the platform matures. We will update this policy and notify active users of
        material changes.
      </p>

      <h2 style={h2Style}>1. Information We Collect</h2>
      <p style={pStyle}><strong>Account information.</strong> When you create an account we collect your name,
        email address, and a hashed version of your password. We never store your password in
        plaintext.</p>
      <p style={pStyle}><strong>Home and property data.</strong> To provide maintenance recommendations we
        collect information you enter about your home: address, year built, square footage, number
        of bedrooms and bathrooms, roof type, foundation type, appliances, and climate zone.</p>
      <p style={pStyle}><strong>Task and maintenance records.</strong> We store the maintenance tasks,
        completion history, notes, and cost records you create within the app.</p>
      <p style={pStyle}><strong>Photos and documents.</strong> Photos and documents you upload (appliance
        manuals, insurance certificates, inspection reports) are stored in Cloudflare R2 object
        storage and linked to your account.</p>
      <p style={pStyle}><strong>Contractor profile data.</strong> If you register as a contractor, we
        collect your business name, owner name, email, phone number, trade category, license
        number, license state, insurance certificate, and service ZIP codes.</p>
      <p style={pStyle}><strong>Usage and device data.</strong> We collect standard server logs including
        IP addresses, request timestamps, browser or app version, and operating system. We use
        this data for security monitoring, debugging, and improving the Service.</p>
      <p style={pStyle}><strong>Authentication tokens.</strong> We store HttpOnly cookies containing
        encrypted refresh tokens to keep you signed in. These cookies are inaccessible to
        JavaScript and expire automatically.</p>
      <p style={pStyle}><strong>Consent records.</strong> We record timestamps when you accept this
        Privacy Policy, our Terms of Service, and our Early Access Disclaimer at account creation.
        These records are kept as an audit log and are not modified after signup.</p>

      <h2 style={h2Style}>2. How We Use Your Information</h2>
      <ul style={ulStyle}>
        <li>To create and manage your account and authenticate you</li>
        <li>To generate personalized home maintenance schedules and recommendations</li>
        <li>To match homeowners with contractors in the marketplace</li>
        <li>To process contractor lead purchases and subscription billing via Stripe</li>
        <li>To send transactional emails (verification, maintenance reminders, lead notifications)</li>
        <li>To verify contractor license and insurance information through automated checks</li>
        <li>To detect fraud, abuse, and security incidents</li>
        <li>To improve the Service through aggregated, anonymized analytics</li>
        <li>To comply with applicable law</li>
      </ul>

      <h2 style={h2Style}>3. Information Sharing</h2>
      <p style={pStyle}><strong>With contractors you engage.</strong> When you request a contractor through
        the marketplace, we share your name, address, and job description with the contractor you
        select.</p>
      <p style={pStyle}><strong>With Stripe.</strong> Contractor subscription payments and lead-purchase
        billing are processed by Stripe, Inc. Stripe's Privacy Policy governs their handling of
        payment data. We do not store full card numbers.</p>
      <p style={pStyle}><strong>Amazon affiliate program.</strong> Some links within the Service link to
        Amazon products through the Amazon Associates program. Clicking these links may result in
        Housewizrd earning a commission. No personally identifiable information about you is shared
        with Amazon through these links.</p>
      <p style={pStyle}><strong>Service providers.</strong> We use Cloudflare to host our database,
        object storage, and API infrastructure. Resend processes transactional emails on our
        behalf. These providers act as data processors and are contractually bound to handle
        your data securely.</p>
      <p style={pStyle}><strong>Legal requirements.</strong> We may disclose information if required by law,
        court order, or government request, or to protect the safety and rights of Housewizrd,
        our users, or the public.</p>
      <p style={pStyle}><strong>We do not sell your personal information.</strong> We never sell, rent,
        or trade your personal data to third parties for advertising purposes.</p>

      <h2 style={h2Style}>4. Data Storage and Security</h2>
      <p style={pStyle}>Your data is stored on Cloudflare's infrastructure, which operates globally
        including in the United States. Passwords are hashed using Argon2id before storage and
        are never transmitted or stored in plaintext. Access tokens expire after 15 minutes;
        refresh tokens expire after 7 days.</p>
      <p style={pStyle}>We implement rate limiting, secure cookie settings (HttpOnly, Secure,
        SameSite=Strict), parameterized SQL queries, and strict CORS policies. Despite these
        measures, no system is perfectly secure. Please use a strong, unique password and enable
        two-factor authentication where available.</p>

      <h2 style={h2Style}>5. Cookies and Local Storage</h2>
      <p style={pStyle}>We use one HttpOnly session cookie (<code>hg_rt</code>) for authentication. This
        cookie is essential for the Service to function and cannot be disabled without preventing
        login. We do not use advertising, tracking, or analytics cookies.</p>
      <p style={pStyle}>The app uses browser localStorage to cache your authentication state between
        sessions. This data stays on your device and is cleared when you log out.</p>

      <h2 style={h2Style}>6. Your Rights and Choices</h2>
      <p style={pStyle}>Depending on where you live, you may have the right to:</p>
      <ul style={ulStyle}>
        <li>Access the personal information we hold about you</li>
        <li>Correct inaccurate information in your account (via Settings)</li>
        <li>Delete your account and associated data (via Settings → Danger Zone)</li>
        <li>Export a copy of your data</li>
        <li>Object to or restrict certain processing</li>
      </ul>
      <p style={pStyle}>To exercise rights beyond what's available in-app, contact us at the address
        below. We will respond within 30 days.</p>
      <p style={pStyle}>When you delete your account, we remove your personal data within 30 days,
        subject to retention required by law (e.g., payment records).</p>

      <h2 style={h2Style}>7. California Privacy Rights (CCPA)</h2>
      <p style={pStyle}>California residents may request disclosure of the categories and specific pieces
        of personal information we have collected, the purposes for which we used it, and whether
        we have disclosed it to third parties. You may also request deletion of your personal
        information, subject to certain exceptions. We do not discriminate against users who
        exercise their CCPA rights.</p>

      <h2 style={h2Style}>8. Children's Privacy</h2>
      <p style={pStyle}>The Service is intended for users 18 years of age or older. We do not
        knowingly collect personal information from children under 13. If you believe we have
        inadvertently collected information from a child, please contact us and we will delete it
        promptly.</p>

      <h2 style={h2Style}>9. Third-Party Links</h2>
      <p style={pStyle}>The Service may contain links to third-party websites (Amazon, app stores,
        contractor websites). This policy applies only to Housewizrd. We are not responsible for the
        privacy practices of third-party sites and encourage you to review their policies.</p>

      <h2 style={h2Style}>10. Changes to This Policy</h2>
      <p style={pStyle}>We will notify active users by email of material changes to this policy at
        least 14 days before the change takes effect. Your continued use of the Service after the
        effective date constitutes acceptance of the updated policy. The current policy version
        recorded at your account creation is stored in your account as <code>policy_version</code>.</p>

      <h2 style={h2Style}>11. Contact Us</h2>
      <p style={pStyle}>For privacy questions, data requests, or concerns:</p>
      <p style={todoStyle}>TODO: Insert contact email address before App Store submission</p>
      <p style={{ ...pStyle, marginTop: 12 }}>Housewizrd LLC · Nahunta, Georgia, United States</p>
    </div>
  );
}
